import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Mail, Lock, CheckCircle, ArrowLeft, Eye, EyeOff } from 'lucide-react';

import { API_BASE_URL } from '../lib/apiBase';
import { AUTH_ENABLED } from '../lib/featureFlags';
import { MaintenanceNotice } from '../components/common/MaintenanceNotice';

const API_URL = API_BASE_URL;

const ForgotPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  // States for forgot password form
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  // States for reset password form
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Wartemodus: after all hooks have been registered (rules-of-hooks compliant)
  // show a calm notice card instead of an active password-reset form.
  if (!AUTH_ENABLED) {
    return <MaintenanceNotice variant="page" showBackLink testId="forgot-password-maintenance" />;
  }

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      if (response.ok) {
        setSuccess(true);
      } else if (response.status === 429) {
        setError('För många försök. Vänta en stund och försök igen.');
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.detail || 'Något gick fel. Försök igen.');
      }
    } catch (err) {
      // fetch() only throws on network/CORS failure, not on HTTP error status.
      // Give the user a clearer hint than the generic "kontrollera din anslutning".
      if (err && err.name === 'TypeError') {
        setError('Det går inte att nå servern just nu. Försök igen om en stund eller kontakta oss på info@hodema.se.');
      } else {
        setError('Något gick fel. Försök igen eller kontakta oss på info@hodema.se.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword !== confirmPassword) {
      setError('Lösenorden matchar inte. Skriv samma lösenord på båda raderna.');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Lösenordet måste vara minst 6 tecken.');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword })
      });
      
      if (response.ok) {
        setResetSuccess(true);
      } else if (response.status === 429) {
        setError('För många försök. Vänta en stund och försök igen.');
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.detail || 'Något gick fel. Försök igen eller begär en ny återställningslänk.');
      }
    } catch (err) {
      if (err && err.name === 'TypeError') {
        setError('Det går inte att nå servern just nu. Försök igen om en stund eller kontakta oss på info@hodema.se.');
      } else {
        setError('Något gick fel. Försök igen eller begär en ny återställningslänk.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Reset password form (when token is present)
  if (token) {
    if (resetSuccess) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-forest-50 to-white flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">Lösenordet har ändrats!</h2>
                <p className="text-gray-600">Du kan nu logga in med ditt nya lösenord.</p>
                <Button onClick={() => navigate('/login')} className="w-full bg-forest-700 hover:bg-forest-800" data-testid="reset-success-login-btn">
                  Gå till inloggning
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-b from-forest-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-forest-800">Välj nytt lösenord</CardTitle>
            <CardDescription>Ange ditt nya lösenord nedan</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleResetPassword} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="newPassword">Nytt lösenord</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    placeholder="Minst 6 tecken"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                  />
                  <button
                    type="button"
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowNewPassword((p) => !p);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 focus:outline-none cursor-pointer"
                    style={{ pointerEvents: 'auto' }}
                    tabIndex={-1}
                    aria-label={showNewPassword ? "Dölj lösenord" : "Visa lösenord"}
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-5 w-5" style={{ pointerEvents: 'none' }} />
                    ) : (
                      <Eye className="h-5 w-5" style={{ pointerEvents: 'none' }} />
                    )}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Bekräfta lösenord</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Skriv lösenordet igen"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                  />
                  <button
                    type="button"
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowConfirmPassword((p) => !p);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 focus:outline-none cursor-pointer"
                    style={{ pointerEvents: 'auto' }}
                    tabIndex={-1}
                    aria-label={showConfirmPassword ? "Dölj lösenord" : "Visa lösenord"}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5" style={{ pointerEvents: 'none' }} />
                    ) : (
                      <Eye className="h-5 w-5" style={{ pointerEvents: 'none' }} />
                    )}
                  </button>
                </div>
              </div>
              
              <Button type="submit" className="w-full bg-forest-700 hover:bg-forest-800" disabled={loading} data-testid="reset-password-submit">
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Sparar nytt lösenord…
                  </>
                ) : (
                  'Ändra lösenord'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success message after requesting reset
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-forest-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Mail className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Kontrollera din e-post</h2>
              <p className="text-gray-600">
                Om e-postadressen finns hos oss har vi skickat en återställningslänk. Klicka på länken i mejlet för att välja ett nytt lösenord.
              </p>
              <p className="text-sm text-gray-500">
                Kontrollera även din skräppost om du inte ser e-postmeddelandet inom några minuter.
              </p>
              <Button variant="outline" onClick={() => navigate('/login')} className="w-full" data-testid="back-to-login-btn">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Tillbaka till inloggning
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Forgot password form
  return (
    <div className="min-h-screen bg-gradient-to-b from-forest-50 to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl text-forest-800">Glömt lösenord?</CardTitle>
          <CardDescription>
            Ange din e-postadress så skickar vi en länk för att återställa ditt lösenord.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email">E-postadress</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="din@email.se"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            
            <Button type="submit" className="w-full bg-forest-700 hover:bg-forest-800" disabled={loading} data-testid="forgot-password-submit">
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Skickar återställningslänk…
                </>
              ) : (
                'Skicka återställningslänk'
              )}
            </Button>
            
            <Button type="button" variant="ghost" onClick={() => navigate('/login')} className="w-full">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Tillbaka till inloggning
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
